
import { computed, defineComponent, ref } from 'vue';
import Collapsible from '@/components/Collapsible.vue';
import InfoTooltip from '@/components/InfoTooltip.vue';
import EditModal from './EditModal.vue';
import { useRequestDataStore } from '@/store/request-data';
import { CovariateValue } from '@/service/models/request/computation/covariate-value';
import { DrugCovariate } from '@/service/models/response/drug-model-detail/covariate/drug-covariate';
import CovariateRow from './CovariateRow.vue';
import CovariateHistoryRow from './CovariateHistoryRow.vue';
import ConcentrationChart from '@/components/ConcentrationChart.vue';
import { useChartsStore } from '@/store/charts';
import ChartSettingsModal from '@/components/ChartSettingsModal.vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
    components: {
        ChartSettingsModal,
        Collapsible,
        ConcentrationChart,
        CovariateHistoryRow,
        CovariateRow,
        EditModal,
        InfoTooltip,
    },

    setup()
    {
        const i18n = useI18n();
        const isCovariateModalOpen = ref(false);
        const isSettingsModalOpen = ref(false);
        const modalData = ref<CovariateValue|null>(null);

        const requestData = useRequestDataStore();
        const chartStore = useChartsStore();

        const covariates = requestData.selectedDrugModel?.model.covariates ?? [];
        const selectedCovariate = ref<DrugCovariate|null>(covariates[0] ?? null);

        // Provide an array of covariates values sorted by date
        const selectedCovariateHistory = computed(() => {
            return (requestData.covariates.get(selectedCovariate.value?.id ?? "") ?? [])
                .sort((a, b) => (a.date.getTime() - b.date.getTime()));
        });

        function getMessageIfCovariateDisabled(covariate: DrugCovariate|null): string|null
        {
            if (covariate === null) {
                return i18n.t("covariates.form.disabledNoCovariateSelected");
            }

            if (covariate.id === "age") {
                return i18n.t("covariates.form.disabledAge");
            }

            return null;
        }

        function removeCovariateValue(covariate: DrugCovariate|null, valueUuid: string): void
        {
            requestData.removeCovariateByUuid(covariate?.id ?? '', valueUuid);
        }

        function openCovariateModal(covariate: DrugCovariate|null, covariateValue: CovariateValue|null): void
        {
            const messageIfDisabled = getMessageIfCovariateDisabled(covariate);

            if (messageIfDisabled !== null) {
                alert(messageIfDisabled);
                return;
            }

            if (covariate === null) {
                return;
            }

            modalData.value = (covariateValue === null)
                ? CovariateValue.makeFromCovariateDefaults(covariate)
                : covariateValue;

            isCovariateModalOpen.value = true;
        }

        function openSettingsModal(): void
        {
            isSettingsModalOpen.value = true;
        }

        function handleSave(payload: CovariateValue)
        {
            // Save data in the store
            for (const covariateValue of requestData.covariates.get(selectedCovariate.value?.id ?? "") ?? []) {
                if (covariateValue.uuid === payload.uuid) {
                    covariateValue.value = payload.value;
                    covariateValue.date = payload.date;
                    return;
                }
            }

            // If the value was not found, insert it
            requestData.addCovariateValue(payload);
        }

        return {
            covariates,
            requestData,
            chartStore,
            selectedCovariate,
            selectedCovariateHistory,
            
            getMessageIfCovariateDisabled,
            removeCovariateValue,
            openCovariateModal,
            isCovariateModalOpen,
            modalData,
            handleSave,

            openSettingsModal,
            isSettingsModalOpen,
        };
    },
})
